import { API, BACKEND_URL } from '@/config/api';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

/**
 * Format date to YYYY-MM-DD in local timezone (not UTC)
 */
const formatDateLocal = (date) => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Generate AI summary for the samples using GPT-5.2 with reference reports
 */
export const generateAISummaryHelper = async ({
  sampleDate,
  selectedArea,
  areas,
  gridData,
  zooplanktonGridData,
  organisms,
  customOrganisms,
  setGeneratedSummary,
  setEditableSummary,
  setShowSummaryStep,
  setGeneratingSummary,
  isDemo = false
}) => {
  setGeneratingSummary(true);
  setShowSummaryStep(true);
  
  const areaName = areas.find(a => a.id === selectedArea)?.name || selectedArea;
  const dateStr = formatDateLocal(sampleDate);
  
  // Extract non-zero algae entries for AI analysis
  const samples = [];
  Object.entries(gridData).forEach(([organismKey, locationData]) => {
    const [group, species] = organismKey.split('_');
    const organism = [...organisms, ...customOrganisms].find(o => o.group === group && o.species === species);
    if (!organism || organism.isSum || organism.isTotal) return;
    
    Object.entries(locationData).forEach(([location, abundance]) => {
      if (abundance && typeof abundance === 'string' && abundance.trim()) {
        const locationName = location ? (location.split(' - ')[1] || location) : 'ukjent';
        samples.push({ group, species, location: locationName, abundance });
      }
    });
  });
  
  // Extract zooplankton data
  const zooSamples = [];
  if (zooplanktonGridData) {
    Object.entries(zooplanktonGridData).forEach(([key, locationData]) => {
      const parts = key.split('_');
      const sampleType = parts[0];
      const organism = parts.slice(1).join('_');
      
      Object.entries(locationData).forEach(([location, value]) => {
        if (value && typeof value === 'string' && value.trim() && value.trim() !== '<5') {
          const locationName = location ? (location.split(' - ')[1] || location) : 'ukjent';
          zooSamples.push({ sampleType, organism, location: locationName, value });
        }
      });
    });
  }
  
  if (isDemo) {
    setTimeout(() => {
      const demoSummary = `## Algeanalyse - ${areaName}\n**Dato:** ${sampleDate.toLocaleDateString('nb-NO')}\n\n### Oppsummering\n**Alger:**\nLåge til moderate verdiar av kiselalger i heile området. **Skeletonema** er dominerande med innslag av **Chaetoceros** og **Pseudo-Nitzschia**. Ikkje nivå som normalt er forbunde med reaksjonar hjå fisk. I vekene framover vil oppblomstringa truleg skyte fart og normalt nå ganske høge nivå. Reduksjon i sikt vil vera ein indikasjon på slik utvikling.\n\nSporadisk registrering av svepeflagellater (**Chrysochromulina/Prymnesium**) i låge konsentrasjonar. Ikkje antatt skadlege nivå nå.\n\n**Zooplankton-irritantar:**\nLåge nivå med sporadisk innslag av mikromaneter og larver av pigghud.`;
      setGeneratedSummary(demoSummary);
      setEditableSummary(demoSummary);
      setGeneratingSummary(false);
    }, 2000);
    return;
  }
  
  try {
    const response = await axios.post(
      `${API}/reports/generate-ai-report`,
      {
        sample_data: { samples, zoo_samples: zooSamples, date: dateStr, area: areaName },
        area_name: areaName,
        sample_date: dateStr
      },
      { withCredentials: true }
    );
    
    const summary = response.data?.report || 'Kunne ikkje generere rapport';
    setGeneratedSummary(summary);
    setEditableSummary(summary);
    setGeneratingSummary(false);
  } catch (error) {
    console.error('Error generating report:', error);
    toast.error('Kunne ikkje generere rapport. Prøv igjen.');
    setGeneratingSummary(false);
  }
};

/**
 * Save without sending to customer (draft mode)
 */
export const saveWithoutSendingHelper = async ({
  isEditMode,
  bulkId,
  sampleDate,
  selectedArea,
  areas,
  gridData,
  zooplanktonGridData,
  colorOverrides,
  temperatureData,
  editableSummary,
  validateAllFieldsFilled,
  clearDraft,
  navigate,
  setLoading,
  isDemo = false,
  currentDraftId,
  userName,
  currentLocations
}) => {
  try {
    setLoading(true);
    
    if (!selectedArea) {
      toast.error('Vel område først');
      setLoading(false);
      return;
    }
    
    // Create bulk metadata
    const bulkIdToUse = isEditMode && bulkId ? bulkId : `bulk-${Date.now()}`;
    const bulkMetadata = {
      id: bulkIdToUse,
      date: formatDateLocal(sampleDate),
      area_id: selectedArea,
      area_name: areas.find(a => a.id === selectedArea)?.name || selectedArea,
      biologist: userName || 'Biolog',
      sample_count: Object.values(gridData)[0] ? Object.keys(Object.values(gridData)[0]).length : 0,
      status: 'draft',
      ai_summary: editableSummary || '',
      algae_data: gridData,
      zooplankton_data: zooplanktonGridData,
      color_overrides: colorOverrides,
      temperature_data: temperatureData || {},
      current_locations: currentLocations || [],
      created_at: new Date().toISOString()
    };
    
    if (isDemo) {
      // Demo mode
      const existingBulks = JSON.parse(localStorage.getItem('demo_bulk_registrations') || '[]');
      
      if (isEditMode && bulkId) {
        const index = existingBulks.findIndex(b => b.id === bulkId);
        if (index !== -1) {
          existingBulks[index] = bulkMetadata;
        }
      } else {
        existingBulks.unshift(bulkMetadata);
      }
      
      localStorage.setItem('demo_bulk_registrations', JSON.stringify(existingBulks));
      
      setTimeout(() => {
        clearDraft();
        toast.success('Prøve lagra som utkast!');
        navigate('/admin/bulk-history');
      }, 1000);
      return;
    }
    
    // Real mode
    if (isEditMode && bulkId) {
      await axios.put(`${API}/bulk-registrations/${bulkId}`, bulkMetadata, { withCredentials: true });
      toast.success('Prøve oppdatert!');
    } else {
      const response = await axios.post(`${API}/bulk-registrations`, bulkMetadata, { withCredentials: true });
      const createdId = response.data?.id || bulkIdToUse;
      toast.success('Prøve lagra som utkast!');
      
      // Clean up the draft entry (but DON'T reset form state — auto-save would overwrite the registration)
      try {
        if (currentDraftId) {
          await axios.delete(`${API}/bulk-drafts/${currentDraftId}`, { withCredentials: true }).catch(() => {});
        }
      } catch {}
      
      // Navigate to the edit page so user can continue editing the SAME registration
      navigate(`/admin/bulk-edit/${createdId}`);
      setLoading(false);
      return;
    }
    
    clearDraft();
    navigate('/admin/bulk-history');
    
  } catch (error) {
    console.error('❌ Save error:', error);
    toast.error('Feil ved lagring av prøve');
    setLoading(false);
  }
};

/**
 * Final save and send to customer
 */
export const finalSaveHelper = async ({
  isEditMode,
  bulkId,
  sampleDate,
  selectedArea,
  areas,
  gridData,
  zooplanktonGridData,
  colorOverrides,
  temperatureData,
  editableSummary,
  organisms,
  customOrganisms,
  clearDraft,
  navigate,
  setLoading,
  isDemo = false,
  userName,
  currentLocations
}) => {
  setLoading(true);
  
  try {
    const samples = [];
    
    // Convert grid data to individual samples
    Object.entries(gridData).forEach(([organismKey, locationData]) => {
      const [group, species] = organismKey.split('_');
      const organism = [...organisms, ...customOrganisms].find(o => o.group === group && o.species === species);
      
      if (!organism || organism.isSum || organism.isTotal) return;
      
      Object.entries(locationData).forEach(([location, abundance]) => {
        if (abundance && abundance.trim()) {
          let biomass = null;
          if (abundance.startsWith('<')) {
            biomass = 5.0;
          } else {
            biomass = parseFloat(abundance) / 1000;
          }
          
          samples.push({
            date: formatDateLocal(sampleDate),
            location_name: location,
            species: group,
            subspecies: species,
            biomass: biomass,
            remarks: `${editableSummary}\n\n---\nBulk entry: ${abundance} ant/l (${selectedArea})`,
            area_id: selectedArea
          });
        }
      });
    });
    
    // Create bulk metadata for history
    const bulkIdToUse = isEditMode && bulkId ? bulkId : `bulk-${Date.now()}`;
    const bulkMetadata = {
      id: bulkIdToUse,
      date: formatDateLocal(sampleDate),
      area_id: selectedArea,
      area_name: areas.find(a => a.id === selectedArea)?.name || selectedArea,
      biologist: userName || 'Biolog',
      sample_count: Object.values(gridData)[0] ? Object.keys(Object.values(gridData)[0]).length : 0,
      status: 'sent',
      ai_summary: editableSummary,
      algae_data: gridData,
      zooplankton_data: zooplanktonGridData,
      color_overrides: colorOverrides,
      temperature_data: temperatureData || {},
      current_locations: currentLocations || [],
      created_at: new Date().toISOString()
    };
    
    if (isDemo) {
      // Save bulk metadata to localStorage
      const existingBulks = JSON.parse(localStorage.getItem('demo_bulk_registrations') || '[]');
      
      if (isEditMode && bulkId) {
        const index = existingBulks.findIndex(b => b.id === bulkId);
        if (index !== -1) {
          existingBulks[index] = bulkMetadata;
        }
      } else {
        existingBulks.unshift(bulkMetadata);
      }
      
      localStorage.setItem('demo_bulk_registrations', JSON.stringify(existingBulks));
      
      setTimeout(() => {
        clearDraft();
        toast.success(`Bulk registrering ${isEditMode ? 'oppdatert' : 'sendt til kunde'}! ${samples.length} prøver registrert.`);
        navigate('/admin/bulk-history');
      }, 1000);
      return;
    }
    
    // Real API calls for production — try batch first, fall back to individual
    if (samples.length > 0) {
      try {
        await axios.post(`${API}/samples/batch`, { samples }, { withCredentials: true });
      } catch (batchErr) {
        if (batchErr.response?.status === 404) {
          // Batch endpoint not available (older backend) — fall back to individual calls
          for (const sample of samples) {
            await axios.post(`${API}/samples`, sample, { withCredentials: true });
          }
        } else {
          throw batchErr;
        }
      }
    }
    
    if (isEditMode && bulkId) {
      await axios.put(`${API}/bulk-registrations/${bulkId}`, bulkMetadata, { withCredentials: true });
      toast.success(`Oppdatert ${samples.length} prøver!`);
    } else {
      await axios.post(`${API}/bulk-registrations`, bulkMetadata, { withCredentials: true });
      toast.success(`Lagret ${samples.length} prøver!`);
    }
    
    clearDraft();
    navigate('/admin/bulk-history');
    
  } catch (error) {
    console.error('❌ Final save error:', error);
    toast.error(`Feil ved lagring av prøver: ${error.response?.status || 'Ukjent feil'}`);
    setLoading(false);
  }
};
